/**
 * Bilingual "English (Deutsch)" label builder for billing categories and
 * features (EB-3886 / EB-3887).
 *
 * Labels come from the Localization API i18n bag
 * (`globalKey=featureShowname`, `category=defTarif`), which returns a flat
 * `Record<code, label>` whose keys are BOTH feature codes (`gut_gut`,
 * `callsekret`, …) AND group/category codes (`secretarialServiceGroup`,
 * `creditGroup`, …). Two bags are fetched per load:
 *   - English bag    (`language = '2'`)
 *   - German bag     (`language = '1'`) — FIXED German for now (EB-3887);
 *     brand/customer language is deliberately NOT used. To be swapped for the
 *     logged-in user's language once a user-language signal exists.
 *
 * The display string is the English label as primary, with the German label in
 * parentheses ONLY when it exists AND differs from the English one. When the
 * German bag has no distinct entry (German equals English) the two are
 * identical, so we collapse to a single label (no redundant parentheses).
 *
 * When the English bag has no entry for a code, the caller supplies a static
 * fallback (FEATURE_LABELS_EN[code] / categoryLabelForGroup(...) → backend
 * showName → raw code) which is used as the primary English label, while the
 * customer-language bag still contributes a parenthetical when it differs.
 */

/** Flat translation bag: maps a feature/group code to its localized label. */
export type LocalizationBag = ReadonlyMap<string, string>;

/**
 * Shared empty bag. Pass as `bagEn` for codes that have NO reliable English
 * entry in the i18n bag (notably group/category codes — the API returns the
 * German group name for every language), so the caller's curated English
 * `fallback` becomes the primary label instead.
 */
export const EMPTY_LOCALIZATION_BAG: LocalizationBag = new Map();

/**
 * Resolve a single feature/group code to its bilingual display label.
 *
 * @param code      Backend code to look up (feature code or group `name`).
 * @param bagEn     English translation bag (`language = '2'`).
 * @param bagLocal  Secondary-language (German) translation bag.
 * @param fallback  Static English fallback when the English bag misses the
 *                  code (e.g. `FEATURE_LABELS_EN[code]` or the resolved
 *                  category/backend label). Must already be non-empty; the
 *                  caller owns the raw-code-last-resort decision.
 * @returns `"English (Deutsch)"` when both labels exist and differ, otherwise
 *          the single English (or fallback) label.
 */
export function buildBilingualLabel(
  code: string,
  bagEn: LocalizationBag,
  bagLocal: LocalizationBag,
  fallback: string,
): string {
  // Primary English label: bag → caller fallback → raw code. An empty-string
  // fallback is treated as "no fallback" so the raw code remains the last resort.
  const en = (bagEn.get(code) || fallback || code).trim();
  const local = (bagLocal.get(code) ?? '').trim();

  // Only append the customer-language label when it exists AND adds
  // information (differs from the English primary). This also collapses the
  // customer-language-is-English (id 2) case, where both bags are equal.
  if (local.length > 0 && local !== en) {
    return `${en} (${local})`;
  }
  return en;
}
